// src/components/Navbar.jsx
import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link, NavLink } from "react-router-dom";
import { resetOnboardingDraft } from "../api/onboarding";
import { useAuth } from "../context/AuthContext";
import {
  clearOnboardingDraft,
  ONBOARDING_GATE_MESSAGE,
} from "../utils/onboarding";
import {
  getEllyannaAccessGate,
  isGateAllowedPath,
} from "../utils/ellyannaAccessGate";
import { isOwnerOrSuperAdmin } from "../utils/salonRoles";
import { resolveAssetUrl } from "../utils/assetUrl";
import { useUiModal } from "../context/UiModalContext";
import { API_BASE } from "../api/base";
import { createMarketplaceCrossLaunch } from "../api/auth";
import PaymentInOutModal from "./modals/PaymentInOutModal";
import AIAssistantModal from "./modals/AIAssistantModal";
import SBINModal from "./modals/SBINModal";
import { BRAND_ICON } from "../config/brand";
import { useBrand } from "../context/BrandContext";
import { buildHostUrl } from "../config/hostRouting";
import useMyPermissions from "../hooks/useMyPermissions";
import { getEllyannaPostSalonSwitchPath } from "../utils/ellyannaShellRouting";
import {
  getMarketplaceAccessNeededPath,
  getMarketplaceCrossLaunchPath,
} from "../utils/marketplaceShellRouting";
import { canOpenMyBoothShare } from "../utils/marketplaceOperatorAccess";

const MANAGER_ROLES = [
  "admin",
  "host",
  "owner",
  "manager",
  "receptionist",
  "solo_technician",
  "reception",
];
const INDEPENDENT_PROFESSIONAL_ROLE = "independent_professional";
const SALON_SPEAK_PERMISSION_ROLES = new Set([
  "technician",
  "apprentice",
  "solo_technician",
]);

const capitalize = (s = "") =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

const RESET_ACTIVITY_WARNING =
  "Setup can't be reset because this salon already has activity. Please finish setup or contact support.";

const publicBookingLandingLinks = [
  { label: "Features", hash: "#features" },
  { label: "Who it helps", hash: "#who-it-helps" },
  { label: "Included", hash: "#included" },
  { label: "Pricing", hash: "#pricing" },
  { label: "Free trial", hash: "#free-trial" },
];

const NavIcon = ({ children }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-4 w-4 md:h-[18px] md:w-[18px] shrink-0"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const CalendarNavIcon = () => (
  <NavIcon>
    <rect x="3" y="5" width="18" height="16" rx="3" />
    <path d="M8 3v4M16 3v4M3 10h18" />
  </NavIcon>
);

const ReportsNavIcon = () => (
  <NavIcon>
    <path d="M3 20h18" />
    <path d="M6 20v-8M12 20V7M18 20v-5" />
  </NavIcon>
);

const HrNavIcon = () => (
  <NavIcon>
    <circle cx="8" cy="8" r="2.3" />
    <circle cx="15.5" cy="7" r="1.8" />
    <path d="M4.5 16c0-2.3 1.8-4 4-4s4 1.7 4 4" />
    <path d="M13 15.5c.2-1.7 1.4-3 3-3 1.7 0 3 1.3 3 3" />
    <path d="M13.4 10l1.2 1.2 2-2" />
  </NavIcon>
);

const OnlineBookingNavIcon = () => (
  <NavIcon>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a15 15 0 0 1 0 18" />
    <path d="M12 3a15 15 0 0 0 0 18" />
  </NavIcon>
);

const MarketingNavIcon = () => (
  <NavIcon>
    <path d="M3 11v2l10 4V7L3 11z" />
    <path d="M13 9h3a3 3 0 0 1 0 6h-3" />
    <path d="M6 15l1.5 4h2" />
  </NavIcon>
);

const ManageNavIcon = () => (
  <NavIcon>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    <path d="M3 12h18" />
  </NavIcon>
);

const AIAssistantNavIcon = () => (
  <NavIcon>
    <path d="M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 3v-3H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" />
    <path d="M14 9l.8 1.6 1.7.4-1.7.6L14 13l-.8-1.4-1.7-.6 1.7-.4L14 9z" />
  </NavIcon>
);

const SuperAdminNavIcon = () => (
  <NavIcon>
    <path d="M12 3l7 3v6c0 5-3.5 7.5-7 9-3.5-1.5-7-4-7-9V6l7-3z" />
    <path d="M9.5 12.5l1.7 1.7 3.3-3.3" />
  </NavIcon>
);

const NavItemLabel = ({ icon: Icon, label }) => (
  <span className="group relative inline-flex items-center gap-1.5">
    <Icon />
    <span className="hidden lg:inline">{label}</span>
    <span className="sr-only lg:hidden">{label}</span>
    <span className="pointer-events-none absolute left-1/2 top-full z-40 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 lg:hidden">
      {label}
    </span>
  </span>
);

const MobileMenuChevron = ({ isOpen, className = "" }) => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    className={`h-3 w-3 shrink-0 transition-transform duration-150 ${
      isOpen ? "rotate-90" : ""
    } ${className}`}
  >
    <path
      d="M8 5l5 5-5 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SALON_SWITCH_OPTION_STYLE = {
  color: "#0f172a",
  backgroundColor: "#ffffff",
  fontWeight: 600,
};

const formatNavSalonLabel = (salon) => {
  if (!salon) {
    return "";
  }
  const id = salon.id ?? salon.salon_id ?? salon.salonId;
  const name = salon.name ?? salon.salon_name ?? salon.salonName ?? "";
  if (id != null && name) {
    return `Salon ID ${id} - ${name}`;
  }
  if (id != null) {
    return `Salon ID ${id}`;
  }
  return name;
};

const getIndependentProfessionalNumber = (user) => {
  const directValue =
    user?.independent_operator_number ??
    user?.independentOperatorNumber ??
    user?.renter_profile?.independent_operator_number ??
    user?.renterProfile?.independentOperatorNumber;
  const parsedDirect = Number(directValue);
  if (Number.isFinite(parsedDirect) && parsedDirect > 0) {
    return parsedDirect;
  }

  const rawId =
    user?.independent_operator_id ??
    user?.independentOperatorId ??
    user?.renter_profile?.independent_operator_id ??
    user?.renterProfile?.independentOperatorId ??
    "";
  const match = String(rawId).match(/(\d+)$/);
  const parsedFromId = match ? Number(match[1]) : null;
  return Number.isFinite(parsedFromId) && parsedFromId > 0 ? parsedFromId : null;
};

const formatIndependentProfessionalContextLabel = (user, salon) => {
  const professionalNumber = getIndependentProfessionalNumber(user);
  const name =
    salon?.name ??
    salon?.salon_name ??
    salon?.salonName ??
    user?.business_name ??
    user?.full_name ??
    user?.name ??
    "";
  if (professionalNumber) {
    return name
      ? `Professional ID ${professionalNumber} - ${name}`
      : `Professional ID ${professionalNumber}`;
  }
  return name || "";
};

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const runtimeBrand = useBrand();
  const getPublicBookingLandingHref = useCallback(
    (hash) => (location.pathname === "/" ? hash : `/${hash}`),
    [location.pathname]
  );
  const { openNewTicketModal } = useUiModal();
  const {
    user,
    role,
    isAuthenticated,
    logout,
    token,
    accessibleSalons,
    selectSalon,
  } = useAuth();
  const roleKey = (role || "").toLowerCase();
  const [isHrOpen, setIsHrOpen] = useState(false);
  const [isHrTrainingOpen, setIsHrTrainingOpen] = useState(false);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [isManageTicketsOpen, setIsManageTicketsOpen] = useState(false);
  const [isOnlineBookingOpen, setIsOnlineBookingOpen] = useState(false);
  const [isMarketingOpen, setIsMarketingOpen] = useState(false);
  const [isMarketingFeedbackOpen, setIsMarketingFeedbackOpen] = useState(false);
  const [isMarketingEmailsOpen, setIsMarketingEmailsOpen] = useState(false);
  const [isSuperAdminOpen, setIsSuperAdminOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [mobileExpandedSections, setMobileExpandedSections] = useState({
    hr: false,
    hrTraining: true,
    techSalonSpeak: true,
    onlineBooking: false,
    marketing: false,
    marketingFeedback: false,
    marketingEmails: false,
    manage: true,
    manageTickets: true,
    superAdmin: false,
    mobileSalonSwitcher: false,
  });
  const [isTechProfileOpen, setIsTechProfileOpen] = useState(false);
  const [isTechSalonSpeakOpen, setIsTechSalonSpeakOpen] = useState(false);
  const [isSwitchingSalon, setIsSwitchingSalon] = useState(false);
  const [paymentModalMode, setPaymentModalMode] = useState(null);
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false);
  const [aiPrefillMessage, setAiPrefillMessage] = useState("");
  const [aiContextOverride, setAiContextOverride] = useState(null);
  const [navGateMsg, setNavGateMsg] = useState("");
  const [showLogoutGate, setShowLogoutGate] = useState(false);
  const [showBlockedNavModal, setShowBlockedNavModal] = useState(false);
  const [showAccessGateModal, setShowAccessGateModal] = useState(false);
  const [staffPhotoUrl, setStaffPhotoUrl] = useState("");
  const manageRef = useRef(null);
  const hrRef = useRef(null);
  const onlineBookingRef = useRef(null);
  const marketingRef = useRef(null);
  const superAdminRef = useRef(null);
  const techProfileRef = useRef(null);
  const accountMenuRef = useRef(null);
  const hrTrainingCloseTimerRef = useRef(null);
  const techSalonSpeakCloseTimerRef = useRef(null);
  const manageTicketsCloseTimerRef = useRef(null);
  const marketingFeedbackCloseTimerRef = useRef(null);
  const marketingEmailsCloseTimerRef = useRef(null);

  const businessId =
    user?.business_id ??
    (typeof window !== "undefined"
      ? Number(window.localStorage.getItem("business_id")) || null
      : null);
  const salonId =
    user?.salon_id ??
    (typeof window !== "undefined"
      ? Number(window.localStorage.getItem("salon_id")) || null
      : null);
  const salonName =
    user?.salon_name ??
    (typeof window !== "undefined" ? window.localStorage.getItem("salon_name") : null);

  const isManager = useMemo(() => {
    const r = (role || "").toLowerCase();
    return MANAGER_ROLES.includes(r);
  }, [role]);
  const isIndependentProfessional = roleKey === INDEPENDENT_PROFESSIONAL_ROLE;
  const { permissions: effectivePermissions } = useMyPermissions({
    enabled: isAuthenticated && Boolean(salonId),
  });
  const hasAnyPermission = useCallback(
    (keys = []) => keys.some((key) => Boolean(effectivePermissions?.[key])),
    [effectivePermissions]
  );
  const hasLoadedPermissions = effectivePermissions !== null;
  const allowByPermissionOrRole = useCallback(
    (keys = [], roleFallback = false) =>
      hasLoadedPermissions ? hasAnyPermission(keys) : roleFallback,
    [hasAnyPermission, hasLoadedPermissions]
  );
  const canManageClients =
    allowByPermissionOrRole(
      ["clients.manage", "clients.view_contact"],
      isManager || isIndependentProfessional
    );
  const canManageTickets =
    allowByPermissionOrRole(
      ["tickets.manage", "tickets.delete", "tickets.reopen", "tickets.refund"],
      isManager || isIndependentProfessional
    );
  const canManageServices =
    allowByPermissionOrRole(["services.manage"], isManager || isIndependentProfessional);
  const canManageStaff = allowByPermissionOrRole(["staff.manage"], isManager);
  const canManageUsers = allowByPermissionOrRole(["users.manage_roles"], isManager);
  const canManageSettings = allowByPermissionOrRole(
    ["settings.manage"],
    isManager || isIndependentProfessional
  );
  const canUseCashDrawer =
    allowByPermissionOrRole(["tickets.manage"], isManager || isIndependentProfessional);
  const canUseInventory = useMemo(
    () =>
      ["admin", "host", "owner", "manager", "receptionist", "reception"].includes(roleKey) ||
      isIndependentProfessional,
    [isIndependentProfessional, roleKey]
  );
  const hasFullScheduleManagerAccess = Boolean(
    effectivePermissions?.["schedule.view_all"] &&
      effectivePermissions?.["schedule.edit"]
  );
  const canUseOperatorSchedule = hasLoadedPermissions
    ? hasFullScheduleManagerAccess
    : isManager || isIndependentProfessional;
  const canUseOperatorNav =
    allowByPermissionOrRole(
      [
        "schedule.view_all",
        "schedule.edit",
        "schedule.cancel",
        "clients.manage",
        "clients.view_contact",
        "emails.manage_opt_outs",
        "tickets.manage",
        "services.manage",
        "staff.manage",
        "users.manage_roles",
        "settings.manage",
        "rewards.view",
        "reports.view",
        "timeclock.view",
        "payroll.view",
        "payroll.manage",
        "training.manage",
      ],
      isManager || isIndependentProfessional
    );
  const canViewReports =
    allowByPermissionOrRole(["reports.view"], isManager || isIndependentProfessional);
  const canViewRewardsProgram =
    allowByPermissionOrRole(["rewards.view"], isManager || isIndependentProfessional);
  const canViewMarketing = useMemo(
    () => ["admin", "host", "owner", "manager"].includes(roleKey) || isIndependentProfessional,
    [isIndependentProfessional, roleKey]
  );
  const canUseCampaigns = useMemo(
    () =>
      isIndependentProfessional ||
      ["manager"].includes(roleKey) ||
      isOwnerOrSuperAdmin(roleKey, user?.is_super_admin),
    [isIndependentProfessional, roleKey, user?.is_super_admin]
  );
  const canUseEmailOptIn = useMemo(
    () =>
      isIndependentProfessional ||
      ["manager"].includes(roleKey) ||
      isOwnerOrSuperAdmin(roleKey, user?.is_super_admin),
    [isIndependentProfessional, roleKey, user?.is_super_admin]
  );
  const canUseEmailOptOuts = useMemo(
    () =>
      allowByPermissionOrRole(
        ["emails.manage_opt_outs"],
        ["admin", "host", "owner", "manager"].includes(roleKey)
      ),
    [allowByPermissionOrRole, roleKey]
  );
  const canViewPayroll = useMemo(() => {
    const r = (role || "").toLowerCase();
    return allowByPermissionOrRole(
      ["payroll.view", "payroll.manage"],
      ["admin", "owner", "manager"].includes(r)
    );
  }, [allowByPermissionOrRole, role]);
  const canManagePayroll = useMemo(() => {
    const r = (role || "").toLowerCase();
    return allowByPermissionOrRole(
      ["payroll.manage"],
      ["admin", "owner", "manager"].includes(r)
    );
  }, [allowByPermissionOrRole, role]);
  const canViewHr = useMemo(() => {
    const r = (role || "").toLowerCase();
    return (
      allowByPermissionOrRole(
        [
          "staff.manage",
          "timeclock.view",
          "timeclock.edit",
          "timeclock.delete",
          "payroll.view",
          "payroll.manage",
          "training.view",
          "training.manage",
        ],
        ["admin", "owner", "manager"].includes(r)
      )
    );
  }, [allowByPermissionOrRole, role]);
  const isTechnician = (role || "").toLowerCase() === "technician";
  const isBusinessOnlySuperAdmin =
    Boolean(user?.is_super_admin) && Boolean(businessId) && !salonId;
  const accessGate = useMemo(
    () => getEllyannaAccessGate(roleKey, user),
    [roleKey, user]
  );
  const onboardingLocked = accessGate?.stage === "setup";
  const canUseAIAssistant =
    isAuthenticated && Boolean(salonId);
  const requiresSalonSpeakPermission = SALON_SPEAK_PERMISSION_ROLES.has(roleKey);
  const canAccessSalonSpeakModules =
    !requiresSalonSpeakPermission ||
    Boolean(effectivePermissions?.["training.view"]);

  const currentPath = location.pathname || "";
  const canLaunchMyBoothShare = canOpenMyBoothShare({ role: roleKey, user });
  const showMyBoothShareLaunch = isAuthenticated;
  const marketplaceSwitchHref = buildHostUrl(
    runtimeBrand.marketplaceHostName,
    canLaunchMyBoothShare
      ? getMarketplaceCrossLaunchPath({
          isAuthenticated,
          role: roleKey,
          user,
        })
      : getMarketplaceAccessNeededPath({
          role: roleKey,
          source: "ellyanna_nav",
        })
  );
  const accountingSwitchHref = buildHostUrl(
    runtimeBrand.accountingHostName || "salonbusinesscpa.com",
    "/login",
    "?next=%2Fsalonbusinesscpa"
  );
  const handleMarketplaceLaunch = useCallback(
    async (event) => {
      if (!isAuthenticated) {
        setIsMenuOpen(false);
        setIsAccountMenuOpen(false);
        return;
      }
      if (!canLaunchMyBoothShare) {
        setIsMenuOpen(false);
        setIsAccountMenuOpen(false);
        return;
      }
      event?.preventDefault();
      event?.stopPropagation();
      setIsMenuOpen(false);
      setIsAccountMenuOpen(false);
      try {
        const launch = await createMarketplaceCrossLaunch();
        const launchPath =
          String(launch?.next_path || "").trim() ||
          getMarketplaceCrossLaunchPath({
            isAuthenticated: true,
            role: roleKey,
            user,
          });
        const url = new URL(
          buildHostUrl(runtimeBrand.marketplaceHostName, launchPath)
        );
        if (launch?.launch_token) {
          url.searchParams.set("launch_token", launch.launch_token);
        }
        window.location.assign(url.toString());
      } catch (err) {
        console.warn("Unable to create marketplace cross launch", err);
        window.location.assign(marketplaceSwitchHref);
      }
    },
    [
      isAuthenticated,
      canLaunchMyBoothShare,
      marketplaceSwitchHref,
      roleKey,
      runtimeBrand.marketplaceHostName,
      user,
    ]
  );
  const isScheduleLayoutRoute =
    currentPath.startsWith("/manager/schedule") ||
    currentPath.startsWith("/technician/schedule");
  const navPillClass = (path) =>
    `px-1 md:px-1.5 lg:px-2 xl:px-2.5 2xl:px-3 min-[1024px]:max-[1500px]:px-1.5 py-1.5 rounded-md text-[11px] lg:text-[12px] 2xl:text-[12px] font-medium inline-flex items-center gap-1 lg:gap-1.5 min-[1024px]:max-[1500px]:gap-1 hover:bg-slate-800 ${
      currentPath.startsWith(path)
        ? "bg-slate-800 text-white"
        : "text-slate-300"
    }`;
  const dropdownButtonClass = (isActive) =>
    `px-1 md:px-1.5 lg:px-2 xl:px-2.5 2xl:px-3 min-[1024px]:max-[1500px]:px-1.5 py-1.5 rounded-md text-[11px] lg:text-[12px] 2xl:text-[12px] font-medium inline-flex items-center gap-1 lg:gap-1.5 min-[1024px]:max-[1500px]:gap-1 hover:bg-slate-800 ${
      isActive ? "bg-slate-800 text-white" : "text-slate-300"
    }`;
  const accountMenuItemClass =
    "block w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-100";
  const accountMenuServiceLinkClass =
    "mt-1 block w-full rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-center text-sm font-semibold text-sky-800 transition hover:bg-sky-100";
  const mobileAccountMenuItemClass =
    "block w-full rounded-md px-3 py-2 text-left text-sm font-semibold text-slate-100 transition hover:bg-white/10";
  const mobileAccountMenuServiceLinkClass =
    "mt-1 block w-full rounded-md border border-sky-400/40 bg-sky-500/10 px-3 py-2 text-center text-sm font-semibold text-sky-100 transition hover:bg-sky-500/20";

  const technicianSetupPath = "/technician/setup";
  const technicianSchedulePath = "/technician/schedule";
  const operatorSchedulePath = canUseOperatorSchedule
    ? "/manager/schedule"
    : technicianSchedulePath;
  const isSuperAdminRouteTarget = (to = "") =>
    typeof to === "string" && to.startsWith("/superadmin");

  const shouldBlockRoute = (to) => {
    if (!to) {
      return false;
    }
    if (accessGate && !isGateAllowedPath(to, accessGate.stage)) {
      return true;
    }
    if (isBusinessOnlySuperAdmin && !isSuperAdminRouteTarget(to)) {
      return true;
    }
    return false;
  };

  const handleBlockedNav = (event, to) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (isBusinessOnlySuperAdmin && !isSuperAdminRouteTarget(to)) {
      setShowBlockedNavModal(true);
      return;
    }
    if (accessGate) {
      setShowAccessGateModal(true);
      return;
    }
    const nonce = Date.now();
    navigate("/setup", {
      state: {
        onboardingGateMessage: ONBOARDING_GATE_MESSAGE,
        onboardingGateNonce: nonce,
        blockedFrom: to,
      },
    });
  };

  const handleNavClick = (to) => (event) => {
    if (shouldBlockRoute(to)) {
      handleBlockedNav(event, to);
    }
  };

  const handleResolveAccessGate = useCallback(() => {
    if (!accessGate?.destination) return;
    setShowAccessGateModal(false);
    if (accessGate.stage === "setup") {
      navigate(accessGate.destination, {
        state: {
          onboardingGateMessage: accessGate.message,
          onboardingGateNonce: Date.now(),
        },
      });
      return;
    }
    navigate(accessGate.destination);
  }, [accessGate, navigate]);

  const handleOpenAIAssistant = useCallback(
    (event, options = {}) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (accessGate) {
        setShowAccessGateModal(true);
        return;
      }
      setAiPrefillMessage(options.prefillMessage || "");
      setAiContextOverride(options.context || null);
      setIsAIAssistantOpen(true);
    },
    [accessGate]
  );

  const clearHrTrainingCloseTimer = () => {
    if (hrTrainingCloseTimerRef.current) {
      clearTimeout(hrTrainingCloseTimerRef.current);
      hrTrainingCloseTimerRef.current = null;
    }
  };

  const openHrTrainingSubmenu = () => {
    clearHrTrainingCloseTimer();
    setIsHrTrainingOpen(true);
  };

  const closeHrTrainingSubmenu = () => {
    clearHrTrainingCloseTimer();
    setIsHrTrainingOpen(false);
  };

  const scheduleCloseHrTrainingSubmenu = () => {
    clearHrTrainingCloseTimer();
    hrTrainingCloseTimerRef.current = setTimeout(() => {
      setIsHrTrainingOpen(false);
      hrTrainingCloseTimerRef.current = null;
    }, 150);
  };

  const closeHrDropdown = () => {
    setIsHrOpen(false);
    closeHrTrainingSubmenu();
  };

  const clearTechSalonSpeakCloseTimer = () => {
    if (techSalonSpeakCloseTimerRef.current) {
      clearTimeout(techSalonSpeakCloseTimerRef.current);
      techSalonSpeakCloseTimerRef.current = null;
    }
  };

  const openTechSalonSpeakSubmenu = () => {
    clearTechSalonSpeakCloseTimer();
    setIsTechSalonSpeakOpen(true);
  };

  const scheduleCloseTechSalonSpeakSubmenu = () => {
    clearTechSalonSpeakCloseTimer();
    techSalonSpeakCloseTimerRef.current = setTimeout(() => {
      setIsTechSalonSpeakOpen(false);
      techSalonSpeakCloseTimerRef.current = null;
    }, 150);
  };

  const clearManageTicketsCloseTimer = () => {
    if (manageTicketsCloseTimerRef.current) {
      clearTimeout(manageTicketsCloseTimerRef.current);
      manageTicketsCloseTimerRef.current = null;
    }
  };

  const openManageTicketsSubmenu = () => {
    clearManageTicketsCloseTimer();
    setIsManageTicketsOpen(true);
  };

  const closeManageTicketsSubmenu = () => {
    clearManageTicketsCloseTimer();
    setIsManageTicketsOpen(false);
  };

  const scheduleCloseManageTicketsSubmenu = () => {
    clearManageTicketsCloseTimer();
    manageTicketsCloseTimerRef.current = setTimeout(() => {
      setIsManageTicketsOpen(false);
      manageTicketsCloseTimerRef.current = null;
    }, 150);
  };

  const closeManageDropdown = () => {
    setIsManageOpen(false);
    closeManageTicketsSubmenu();
  };

  const clearMarketingFeedbackCloseTimer = () => {
    if (marketingFeedbackCloseTimerRef.current) {
      clearTimeout(marketingFeedbackCloseTimerRef.current);
      marketingFeedbackCloseTimerRef.current = null;
    }
  };

  const openMarketingFeedbackSubmenu = () => {
    clearMarketingFeedbackCloseTimer();
    closeMarketingEmailsSubmenu();
    setIsMarketingFeedbackOpen(true);
  };

  const closeMarketingFeedbackSubmenu = () => {
    clearMarketingFeedbackCloseTimer();
    setIsMarketingFeedbackOpen(false);
  };

  const scheduleCloseMarketingFeedbackSubmenu = () => {
    clearMarketingFeedbackCloseTimer();
    marketingFeedbackCloseTimerRef.current = setTimeout(() => {
      setIsMarketingFeedbackOpen(false);
      marketingFeedbackCloseTimerRef.current = null;
    }, 150);
  };

  const clearMarketingEmailsCloseTimer = () => {
    if (marketingEmailsCloseTimerRef.current) {
      clearTimeout(marketingEmailsCloseTimerRef.current);
      marketingEmailsCloseTimerRef.current = null;
    }
  };

  const openMarketingEmailsSubmenu = () => {
    clearMarketingEmailsCloseTimer();
    closeMarketingFeedbackSubmenu();
    setIsMarketingEmailsOpen(true);
  };

  const closeMarketingEmailsSubmenu = () => {
    clearMarketingEmailsCloseTimer();
    setIsMarketingEmailsOpen(false);
  };

  const scheduleCloseMarketingEmailsSubmenu = () => {
    clearMarketingEmailsCloseTimer();
    marketingEmailsCloseTimerRef.current = setTimeout(() => {
      setIsMarketingEmailsOpen(false);
      marketingEmailsCloseTimerRef.current = null;
    }, 150);
  };

  const closeMarketingDropdown = () => {
    setIsMarketingOpen(false);
    closeMarketingFeedbackSubmenu();
    closeMarketingEmailsSubmenu();
  };

  const handleOpenNewTicket = (event) => {
    if (shouldBlockRoute("/tickets/new")) {
      handleBlockedNav(event, "/tickets/new");
      return;
    }
    closeManageDropdown();
    setIsMenuOpen(false);
    openNewTicketModal({ defaultDate: new Date() });
  };

  const handleOpenPaymentModal = (mode) => (event) => {
    if (shouldBlockRoute("/tickets/cash-adjustments")) {
      handleBlockedNav(event, "/tickets/cash-adjustments");
      return;
    }
    closeManageDropdown();
    setIsMenuOpen(false);
    setPaymentModalMode(mode);
  };

  const closePaymentModal = () => {
    setPaymentModalMode(null);
  };

  const handleNavbarSalonChange = async (event) => {
    if (!selectSalon) {
      return;
    }
    const nextId = event.target.value ? Number(event.target.value) : null;
    if (!nextId || nextId === user?.salon_id) {
      return;
    }
    setNavGateMsg("");
    setIsSwitchingSalon(true);
    try {
      const switched = await selectSalon(nextId);
      const nextPath = getEllyannaPostSalonSwitchPath({
        pathname: location.pathname,
        search: location.search,
        role: switched?.role,
        user: switched,
      });
      const currentRoute = `${location.pathname}${location.search || ""}`;
      if (nextPath !== currentRoute) {
        navigate(nextPath, { replace: true });
      }
    } catch (err) {
      console.error("Unable to switch salons", err);
      setNavGateMsg(
        err?.message || "Unable to switch salons. Please try again."
      );
    } finally {
      setIsSwitchingSalon(false);
    }
  };

  const handleCancelSetupAndExit = async () => {
    if (!token) return;
    try {
      await resetOnboardingDraft(token);
    } catch (err) {
      const statusCode = err?.response?.status;
      if (statusCode === 409) {
        setShowLogoutGate(false);
        navigate("/setup", {
          replace: true,
          state: {
            onboardingGateMessage: RESET_ACTIVITY_WARNING,
          },
        });
        return;
      }
      setNavGateMsg("Unable to reset setup. Please try again.");
      return;
    }
    clearOnboardingDraft();
    setShowLogoutGate(false);
    await logout();
    navigate("/login");
  };

  const ticketsMenuItems = useMemo(
    () => [
      { to: "/tickets", label: "Ticket List" },
      { to: "/tickets/new", label: "New Ticket" },
      { action: "payment-in", label: "Payment In" },
      { action: "payment-out", label: "Payment Out" },
      { to: "/tickets/house-sale", label: "House Sale" },
    ],
    []
  );

  const manageMenuItems = useMemo(() => {
    if (isIndependentProfessional) {
      return [
        ...(canManageClients ? [{ to: "/clients", label: "Clients" }] : []),
        { to: "/manager/manage/daily-tasks", label: "Daily Tasks" },
        ...(canUseInventory ? [{ to: "/manager/manage/inventory", label: "Inventory" }] : []),
        { to: "/manager/manage/gift-cards", label: "Gift Cards" },
        ...(canManageTickets
          ? [{ label: "Tickets", type: "submenu", items: ticketsMenuItems }]
          : []),
        ...(canUseCashDrawer ? [{ to: "/manager/cash-drawer", label: "Cash Drawer" }] : []),
        { to: "/profile", label: "My Profile" },
        ...(canManageServices
          ? [{ to: "/manager/manage/services", label: "Services & Products" }]
          : []),
        { to: "/manager/manage/billing", label: "Billing" },
        ...(canManageSettings
          ? [
              {
                to: "/setup?mode=independent_professional",
                label: "Independent Professional Setup",
              },
            ]
          : []),
        { to: "/bug-report", label: "Report a Bug" },
      ];
    }
    const items = [{ to: "/profile", label: "My Profile" }];
    if (canManageClients) {
      items.unshift({ to: "/clients", label: "Clients" });
    }
    items.push({ to: "/manager/manage/daily-tasks", label: "Daily Tasks" });
    if (canUseInventory) {
      items.push({ to: "/manager/manage/inventory", label: "Inventory" });
    }
    if (isManager) {
      items.push({ to: "/manager/manage/gift-cards", label: "Gift Cards" });
    }
    if (canManageTickets) {
      items.push({ label: "Tickets", type: "submenu", items: ticketsMenuItems });
    }
    if (canUseCashDrawer) {
      items.push({ to: "/manager/cash-drawer", label: "Cash Drawer" });
    }
    if (canManageUsers) {
      items.push({ to: "/manager/manage/users", label: "Users & Roles" });
      items.push({ to: "/manager/join-requests", label: "Salon Join Requests" });
    }
    if (canManageServices) {
      items.push({ to: "/manager/manage/services", label: "Services & Products" });
    }
    if (isManager || hasAnyPermission(["staff.manage"])) {
      items.push({ to: "/manager/manage/maintenance-cleaning", label: "Maintenance & Cleaning" });
    }
    if (Boolean(user?.is_super_admin)) {
      items.push({ to: "/admin/business-settings", label: "Business Settings" });
    }
    if (["owner", "admin"].includes(roleKey) || Boolean(user?.is_super_admin)) {
      items.push({ to: "/manager/manage/billing", label: "Billing" });
    }
    if (canManageSettings) {
      items.push({
        to: isIndependentProfessional ? "/setup?mode=independent_professional" : "/setup",
        label: isIndependentProfessional ? "Independent Professional Setup" : "Salon Setup",
      });
    }
    items.push({ to: "/bug-report", label: "Report a Bug" });
    if (Boolean(user?.is_super_admin)) {
      items.push({ to: "/admin/users", label: "Admin Users" });
    }
    return items;
  }, [
    isIndependentProfessional,
    canManageClients,
    canManageServices,
    canManageSettings,
    canManageTickets,
    canManageUsers,
    canUseCashDrawer,
    canUseInventory,
    hasAnyPermission,
    isManager,
    roleKey,
    user?.is_super_admin,
    ticketsMenuItems,
  ]);

  const onlineBookingMenuItems = useMemo(() => {
    if (!["admin", "host", "owner", "manager"].includes(roleKey) && !isIndependentProfessional) {
      return [];
    }
    const items = [
      { to: "/manager/manage/client-portal", label: "Client Portal" },
      {
        to: "/manager/manage/client-portal/ai-assistant",
        label: "Client Portal AI Assistant",
      },
      {
        to: "/manager/online-booking/cancellations",
        label: "Online Cancellations",
      },
      {
        to: "/manager/integrations/google-drive",
        label: "Google Drive Storage",
      },
    ];
    if (salonId) {
      items.push({
        href: `/portal/salon/${salonId}`,
        label: "Preview Portal",
        external: true,
      });
    }
    return items;
  }, [isIndependentProfessional, roleKey, salonId]);

  const marketingMenuItems = useMemo(() => {
    if (!canUseOperatorNav) {
      return [];
    }
    if (!canViewRewardsProgram && !canViewMarketing) {
      return [];
    }
    if (!canViewMarketing) {
      return [{ to: "/manager/rewards", label: "Rewards Program" }];
    }
    const items = [];
    if (canViewRewardsProgram) {
      items.push({ to: "/manager/rewards", label: "Rewards Program" });
    }
    items.push({
        label: "Client Feedback",
        type: "submenu",
        submenuKey: "feedback",
        items: [
          { to: "/manager/marketing/client-reviews", label: "Client Reviews" },
          { to: "/manager/marketing/client-surveys", label: "Client Surveys" },
          { to: "/manager/marketing/feedback-followups", label: "Feedback Follow-ups" },
          { to: "/manager/marketing/response-templates", label: "Response Templates" },
        ],
      });
    if (canUseCampaigns) {
      items.push({ to: "/manager/marketing/campaigns", label: "Campaigns" });
    }
    const emailItems = [];
    if (canUseCampaigns) {
      emailItems.push({
        to: "/manager/marketing/email-reminders",
        label: "Email Reminders",
      });
    }
    if (canUseEmailOptIn) {
      emailItems.push({ to: "/manager/marketing/email-opt-in", label: "Email Opt-In" });
    }
    if (canUseEmailOptOuts) {
      emailItems.push({
        to: "/manager/marketing/email-opt-outs",
        label: "Email Opt-Outs",
      });
    }
    emailItems.push(
      {
        to: "/manager/online-booking/appointment-emails",
        label: "Appointment Emails",
      },
      { to: "/manager/clients/duplicate-emails", label: "Duplicate Emails" }
    );
    if (emailItems.length > 0) {
      items.push({
        label: "Emails",
        type: "submenu",
        submenuKey: "emails",
        items: emailItems,
      });
    }
    return items;
  }, [
    canViewMarketing,
    canViewRewardsProgram,
    canUseOperatorNav,
    canUseCampaigns,
    canUseEmailOptIn,
    canUseEmailOptOuts,
  ]);

  const hrMenuItems = useMemo(() => {
    const items = [];
    if (canManagePayroll) {
      items.push({ to: "/manager/hr/pay-stubs", label: "Pay Stubs" });
    }
    if (
      !isIndependentProfessional &&
      (isManager ||
        hasAnyPermission(["timeclock.view", "timeclock.edit", "timeclock.delete"]))
    ) {
      items.push({ to: "/manager/time-clock", label: "Time Clock" });
    }
    if (isManager || hasAnyPermission(["training.view", "training.manage"])) {
      items.push({ to: "/manager/hr/training", label: "Training & Development" });
    }
    if (canAccessSalonSpeakModules) {
      items.push({
        label: "SalonSpeak",
        type: "submenu",
        items: [
          { to: "/manager/hr/training/salonspeak/home", label: "SalonSpeak English" },
          {
            to: "/manager/hr/training/salonspeak-upsell/home",
            label: "SalonSpeak Upsell",
          },
          {
            to: "/manager/hr/training/salonspeak-reception/home",
            label: "SalonSpeak Reception",
          },
        ],
      });
    }
    if (canViewPayroll) {
      items.push({ to: "/manager/payroll", label: "Payroll" });
    }
    if (!isIndependentProfessional && canManageStaff) {
      items.push({ to: "/manager/manage/staff", label: "Staff" });
    }
    return items;
  }, [
    canViewHr,
    canManagePayroll,
    canViewPayroll,
    canAccessSalonSpeakModules,
    canManageStaff,
    hasAnyPermission,
    isIndependentProfessional,
    isManager,
  ]);

  const superAdminMenuItems = useMemo(
    () => [
      { to: "/superadmin", label: "Console" },
      { to: "/superadmin/reports", label: "Reports" },
      { to: "/superadmin/billing-coupons", label: "Billing Coupons" },
      { to: "/superadmin/billing-risk-alerts", label: "Billing Risk Alerts" },
      { to: "/superadmin/client-assignment", label: "Client Assignment" },
      { to: "/superadmin/registration-activity", label: "Registration Activity" },
      { to: "/superadmin/login-activity", label: "Login Activity" },
    ],
    []
  );
  const desktopManageMenuItems = useMemo(
    () => manageMenuItems.filter((item) => item.to !== "/profile"),
    [manageMenuItems]
  );

  const isManageRoute = useMemo(
    () =>
      desktopManageMenuItems.some((item) => {
        if (item.to && currentPath.startsWith(item.to)) {
          return true;
        }
        if (Array.isArray(item.items)) {
          return item.items.some(
            (child) => child.to && currentPath.startsWith(child.to)
          );
        }
        return false;
      }),
    [desktopManageMenuItems, currentPath]
  );
  const isOnlineBookingRoute = useMemo(
    () =>
      [
        "/manager/manage/client-portal",
        "/manager/manage/client-portal/ai-assistant",
        "/manager/online-booking/cancellations",
        "/manager/integrations/google-drive",
      ].some((path) => currentPath.startsWith(path)),
    [currentPath]
  );
  const isMarketingRoute = useMemo(
    () =>
      currentPath.startsWith("/manager/marketing") ||
      currentPath.startsWith("/manager/rewards") ||
      currentPath.startsWith("/manager/online-booking/appointment-emails") ||
      currentPath.startsWith("/manager/clients/duplicate-emails"),
    [currentPath]
  );
  const isHrRoute = useMemo(() => {
    const matchers = [
      "/manager/hr",
      "/manager/payroll",
    ];
    if (!isIndependentProfessional) {
      matchers.push("/manager/time-clock", "/manager/manage/staff", "/staff/");
    }
    return matchers.some((path) => currentPath.startsWith(path));
  }, [currentPath, isIndependentProfessional]);
  const isSuperAdminRoute = useMemo(
    () => currentPath.startsWith("/superadmin"),
    [currentPath]
  );

  const currentSalonContext = {
    salon_id: salonId,
    salon_name: salonName,
  };
  const salonContextLabel =
    (isIndependentProfessional
      ? formatIndependentProfessionalContextLabel(user, currentSalonContext)
      : formatNavSalonLabel(currentSalonContext)) || "Salon context unavailable";
  const userDisplayName = (
    user?.full_name || user?.name || user?.email || ""
  ).trim();
  const [failedStaffAvatarUrl, setFailedStaffAvatarUrl] = useState("");
  const resolvedStaffAvatarUrl = resolveAssetUrl(user?.photo_url || staffPhotoUrl || "");
  const staffAvatarUrl =
    resolvedStaffAvatarUrl && resolvedStaffAvatarUrl !== failedStaffAvatarUrl
      ? resolvedStaffAvatarUrl
      : "";
  const userInitials = useMemo(() => {
    const source = (userDisplayName || user?.email || "").trim();
    if (!source) {
      return "SB";
    }
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  }, [userDisplayName, user?.email]);
  useEffect(() => {
    setFailedStaffAvatarUrl("");
  }, [resolvedStaffAvatarUrl]);
  const handleStaffAvatarError = (event) => {
    const failedUrl = event?.currentTarget?.currentSrc || event?.currentTarget?.src || "";
    setFailedStaffAvatarUrl(failedUrl || resolvedStaffAvatarUrl);
  };
  const salonContextLabelWithUser = userDisplayName
    ? `${userDisplayName} \u2022 ${salonContextLabel}`
    : salonContextLabel;
  const hideSalonContext =
    !token ||
    location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/register") ||
    !salonId;
  const showSalonContext = isAuthenticated && !hideSalonContext;
  const accessibleSalonList = Array.isArray(accessibleSalons)
    ? accessibleSalons
    : [];
  const showNavbarSalonSelector =
    showSalonContext && accessibleSalonList.length > 1;
  const toggleMobileSection = (sectionKey) => {
    setMobileExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  useEffect(() => {
    if (!isAuthenticated || !token || !salonId) {
      setStaffPhotoUrl("");
      return;
    }
    let cancelled = false;
    const loadStaffPhoto = async () => {
      try {
        const response = await fetch(`${API_BASE}/staffs/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          if (!cancelled) setStaffPhotoUrl("");
          return;
        }
        const staffProfile = await response.json();
        if (cancelled) return;
        setStaffPhotoUrl(resolveAssetUrl(staffProfile?.photo_url || ""));
      } catch {
        if (!cancelled) setStaffPhotoUrl("");
      }
    };
    loadStaffPhoto();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token, salonId, user?.id]);


  const handleLogout = async () => {
    setIsMenuOpen(false);
    setIsAccountMenuOpen(false);
    if (onboardingLocked) {
      setShowLogoutGate(true);
      return;
    }
    await logout();
    navigate("/login");
  };

  const handleGoToSuperAdminConsole = () => {
    setShowBlockedNavModal(false);
    navigate("/superadmin");
  };

  useEffect(() => {
    if (!onboardingLocked) {
      setShowLogoutGate(false);
    }
  }, [onboardingLocked]);

  useEffect(() => {
    if (!isBusinessOnlySuperAdmin) {
      setShowBlockedNavModal(false);
    }
  }, [isBusinessOnlySuperAdmin]);

  useEffect(() => {
    if (!accessGate) {
      setShowAccessGateModal(false);
    }
  }, [accessGate]);

  const closeMenus = () => {
    closeHrDropdown();
    setIsManageOpen(false);
    closeManageTicketsSubmenu();
    setIsOnlineBookingOpen(false);
    closeMarketingDropdown();
    setIsSuperAdminOpen(false);
    setIsTechProfileOpen(false);
    setIsAccountMenuOpen(false);
    clearTechSalonSpeakCloseTimer();
    setIsTechSalonSpeakOpen(false);
  };

  useEffect(() => {
    setIsAccountMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isTechProfileOpen) {
      if (techSalonSpeakCloseTimerRef.current) {
        clearTimeout(techSalonSpeakCloseTimerRef.current);
        techSalonSpeakCloseTimerRef.current = null;
      }
      setIsTechSalonSpeakOpen(false);
    }
  }, [isTechProfileOpen]);

  useEffect(() => {
    const closeOnClickOutside = (event, ref, setter) => {
      if (!setter || !ref.current) return;
      if (ref.current.contains(event.target)) return;
      setter(false);
    };

    const handleClick = (event) => {
      if (manageRef.current && !manageRef.current.contains(event.target)) {
        closeManageDropdown();
      }
      if (hrRef.current && !hrRef.current.contains(event.target)) {
        closeHrDropdown();
      }
      closeOnClickOutside(event, onlineBookingRef, setIsOnlineBookingOpen);
      if (marketingRef.current && !marketingRef.current.contains(event.target)) {
        closeMarketingDropdown();
      }
      closeOnClickOutside(event, superAdminRef, setIsSuperAdminOpen);
      closeOnClickOutside(event, techProfileRef, setIsTechProfileOpen);
      closeOnClickOutside(event, accountMenuRef, setIsAccountMenuOpen);
    };

    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, []);

  useEffect(() => {
    return () => {
      clearHrTrainingCloseTimer();
      clearTechSalonSpeakCloseTimer();
      clearManageTicketsCloseTimer();
      clearMarketingFeedbackCloseTimer();
      clearMarketingEmailsCloseTimer();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeManageDropdown();
        closeHrDropdown();
        setIsOnlineBookingOpen(false);
        closeMarketingDropdown();
        setIsSuperAdminOpen(false);
        setIsTechProfileOpen(false);
        setIsAccountMenuOpen(false);
        if (techSalonSpeakCloseTimerRef.current) {
          clearTimeout(techSalonSpeakCloseTimerRef.current);
          techSalonSpeakCloseTimerRef.current = null;
        }
        setIsTechSalonSpeakOpen(false);
        setIsMenuOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (!navGateMsg) return;
    const timeout = setTimeout(() => setNavGateMsg(""), 2500);
    return () => clearTimeout(timeout);
  }, [navGateMsg]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (!isMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleOpenAi = (event) => {
      const detail = event?.detail || {};
      const prefill =
        typeof detail.prefillMessage === "string" ? detail.prefillMessage : "";
      const context =
        detail?.context && typeof detail.context === "object"
          ? detail.context
          : null;
      handleOpenAIAssistant(null, {
        prefillMessage: prefill,
        context,
      });
    };
    window.addEventListener("sbin:open-ai-assistant", handleOpenAi);
    return () => {
      window.removeEventListener("sbin:open-ai-assistant", handleOpenAi);
    };
  }, [handleOpenAIAssistant]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 md:sticky md:top-0 ${
          isMenuOpen ? "z-[4700]" : "z-[3000]"
        } bg-[#2f3566] text-white shadow-sm`}
      >
      <div
        className={`h-[calc(64px+env(safe-area-inset-top,0px))] pt-[env(safe-area-inset-top,0px)] flex items-center gap-1 md:gap-1.5 lg:gap-1.5 2xl:gap-2 min-[1024px]:max-[1500px]:gap-1 whitespace-nowrap ${
          isScheduleLayoutRoute
            ? "w-full pl-1 pr-1.5 lg:pl-2 lg:pr-2"
            : "mx-auto max-w-[1400px] px-4"
        }`}
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 lg:gap-3 min-[1024px]:max-[1500px]:gap-2 flex-shrink-0"
          onClick={handleNavClick("/")}
          aria-label="Ellyanna home"
        >
          <img
            src={BRAND_ICON}
            alt="Ellyanna"
            className={`${isScheduleLayoutRoute ? "h-9 w-9" : "h-10 w-10"} flex-shrink-0`}
            style={{ display: "block" }}
          />
          <div
            className="ellyanna-hide-under-420 min-w-0 leading-none"
            style={{
              WebkitTextStroke: "0 transparent",
              textShadow: "none",
              filter: "none",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
            }}
          >
            <div
              className={`text-white font-semibold ${
                isScheduleLayoutRoute
                  ? "text-base xl:text-[19px]"
                  : "text-base lg:text-xl"
              }`}
            >
              Ellyanna
            </div>
            <div className="mt-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-300 lg:text-[10px] lg:tracking-[0.22em]">
              Booking
            </div>
          </div>
        </Link>

        <div
          className={`hidden md:flex flex-1 items-center gap-1 md:gap-1.5 lg:gap-1.5 2xl:gap-2 min-[1024px]:max-[1500px]:gap-1 whitespace-nowrap ${
            isScheduleLayoutRoute
              ? "justify-start pl-2 lg:pl-6 xl:pl-24"
              : !isAuthenticated
              ? "justify-start pl-8 lg:pl-12"
              : "justify-center"
          }`}
        >
          {!isAuthenticated &&
            publicBookingLandingLinks.map((item) => (
              <a
                key={item.hash}
                href={getPublicBookingLandingHref(item.hash)}
                className="inline-flex h-9 shrink-0 items-center rounded-md px-3 text-sm font-semibold text-slate-200 transition hover:bg-slate-800 hover:text-white"
              >
                {item.label}
              </a>
            ))}

          {isAuthenticated && canUseOperatorNav && (
            <>
              <Link
                to={operatorSchedulePath}
                className={navPillClass(operatorSchedulePath)}
                onClick={handleNavClick(operatorSchedulePath)}
              >
                <NavItemLabel icon={CalendarNavIcon} label="Schedule" />
              </Link>
              {canViewReports && (
                <Link
                  to="/manager/reports"
                  className={navPillClass("/manager/reports")}
                  onClick={(e) => {
                    closeMenus();
                    handleNavClick("/manager/reports")(e);
                  }}
                >
                  <NavItemLabel icon={ReportsNavIcon} label="Reports" />
                </Link>
              )}
              {hrMenuItems.length > 0 && (
                <div className="relative" ref={hrRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsHrOpen((prev) => {
                        const next = !prev;
                        if (!next) {
                          closeHrTrainingSubmenu();
                        }
                        return next;
                      });
                      setIsManageOpen(false);
                      closeManageTicketsSubmenu();
                      setIsOnlineBookingOpen(false);
                      closeMarketingDropdown();
                      setIsSuperAdminOpen(false);
                    }}
                    className={dropdownButtonClass(isHrOpen || isHrRoute)}
                  >
                    <NavItemLabel icon={HrNavIcon} label="HR" />
                  </button>
                  {isHrOpen && (
                    <div className="absolute left-0 top-10 w-60 max-w-[260px] rounded-lg bg-white text-slate-900 shadow-lg border border-slate-200 z-30">
                      {hrMenuItems.map((item) => {
                        if (item.type === "submenu") {
                          return (
                            <div
                              key={`hr-${item.label}`}
                              className="relative"
                              onMouseEnter={openHrTrainingSubmenu}
                              onMouseLeave={scheduleCloseHrTrainingSubmenu}
                            >
                              <NavLink
                                to={item.to}
                                onClick={(e) => {
                                  closeHrDropdown();
                                  handleNavClick(item.to)(e);
                                }}
                                onFocus={openHrTrainingSubmenu}
                                className="flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-100"
                              >
                                <span>{item.label}</span>
                                <span className="text-slate-400">&rsaquo;</span>
                              </NavLink>
                              {isHrTrainingOpen && (
                                <div
                                  className="absolute left-full top-0 ml-2 w-56 rounded-lg bg-white text-slate-900 shadow-lg border border-slate-200 z-40"
                                  onMouseEnter={openHrTrainingSubmenu}
                                  onMouseLeave={scheduleCloseHrTrainingSubmenu}
                                >
                                  {item.items.map((child) => (
                                    <NavLink
                                      key={`${child.to}-${child.label}`}
                                      to={child.to}
                                      onClick={(e) => {
                                        closeHrDropdown();
                                        handleNavClick(child.to)(e);
                                      }}
                                      className="block px-3 py-2 text-sm hover:bg-slate-100"
                                    >
                                      {child.label}
                                    </NavLink>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        }
                        return (
                          <NavLink
                            key={`${item.to}-${item.label}`}
                            to={item.to}
                            onClick={(e) => {
                              closeHrDropdown();
                              handleNavClick(item.to)(e);
                            }}
                            className="block px-3 py-2 text-sm hover:bg-slate-100"
                          >
                            {item.label}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {onlineBookingMenuItems.length > 0 && (
                <div className="relative" ref={onlineBookingRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsOnlineBookingOpen((prev) => !prev);
                      closeHrDropdown();
                      setIsManageOpen(false);
                      closeManageTicketsSubmenu();
                      closeMarketingDropdown();
                      setIsSuperAdminOpen(false);
                    }}
                    className={dropdownButtonClass(
                      isOnlineBookingOpen || isOnlineBookingRoute
                    )}
                  >
                    <NavItemLabel
                      icon={OnlineBookingNavIcon}
                      label="Online Booking"
                    />
                  </button>
                  {isOnlineBookingOpen && (
                    <div className="absolute left-0 top-10 w-60 max-w-[260px] rounded-lg bg-white text-slate-900 shadow-lg border border-slate-200 z-30">
                      {onlineBookingMenuItems.map((item) => {
                        if (item.href) {
                          return (
                            <a
                              key={`${item.href}-${item.label}`}
                              href={item.href}
                              target={item.external ? "_blank" : undefined}
                              rel={item.external ? "noreferrer" : undefined}
                              onClick={(event) => {
                                if (shouldBlockRoute(item.href)) {
                                  handleBlockedNav(event, item.href);
                                  return;
                                }
                                setIsOnlineBookingOpen(false);
                              }}
                              className="block px-3 py-2 text-sm hover:bg-slate-100 truncate"
                            >
                              {item.label}
                            </a>
                          );
                        }
                        return (
                          <NavLink
                            key={`${item.to}-${item.label}`}
                            to={item.to}
                            onClick={(e) => {
                              setIsOnlineBookingOpen(false);
                              handleNavClick(item.to)(e);
                            }}
                            className="block px-3 py-2 text-sm hover:bg-slate-100 truncate"
                          >
                            {item.label}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {marketingMenuItems.length > 0 && (
                <div className="relative" ref={marketingRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMarketingOpen((prev) => !prev);
                      closeMarketingFeedbackSubmenu();
                      closeMarketingEmailsSubmenu();
                      closeHrDropdown();
                      setIsManageOpen(false);
                      closeManageTicketsSubmenu();
                      setIsOnlineBookingOpen(false);
                      setIsSuperAdminOpen(false);
                    }}
                    className={dropdownButtonClass(
                      isMarketingOpen || isMarketingRoute
                    )}
                  >
                    <NavItemLabel icon={MarketingNavIcon} label="Marketing" />
                  </button>
                  {isMarketingOpen && (
                    <div className="absolute left-0 top-10 w-56 rounded-lg bg-white text-slate-900 shadow-lg border border-slate-200 z-30">
                      {marketingMenuItems.map((item) => {
                        if (item.type === "submenu") {
                          const isEmailsSubmenu = item.submenuKey === "emails";
                          const isSubmenuOpen = isEmailsSubmenu
                            ? isMarketingEmailsOpen
                            : isMarketingFeedbackOpen;
                          const openSubmenu = isEmailsSubmenu
                            ? openMarketingEmailsSubmenu
                            : openMarketingFeedbackSubmenu;
                          const scheduleCloseSubmenu = isEmailsSubmenu
                            ? scheduleCloseMarketingEmailsSubmenu
                            : scheduleCloseMarketingFeedbackSubmenu;
                          return (
                            <div
                              key={`marketing-${item.label}`}
                              className="relative"
                              onMouseEnter={openSubmenu}
                              onMouseLeave={scheduleCloseSubmenu}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  if (isEmailsSubmenu) {
                                    setIsMarketingEmailsOpen((prev) => {
                                      const next = !prev;
                                      if (next) closeMarketingFeedbackSubmenu();
                                      return next;
                                    });
                                  } else {
                                    setIsMarketingFeedbackOpen((prev) => {
                                      const next = !prev;
                                      if (next) closeMarketingEmailsSubmenu();
                                      return next;
                                    });
                                  }
                                }}
                                onFocus={openSubmenu}
                                className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-slate-100"
                              >
                                <span>{item.label}</span>
                                <span className="text-slate-400">&rsaquo;</span>
                              </button>
                              {isSubmenuOpen && (
                                <div
                                  className="absolute left-full top-0 ml-2 w-60 rounded-lg bg-white text-slate-900 shadow-lg border border-slate-200 z-40"
                                  onMouseEnter={openSubmenu}
                                  onMouseLeave={scheduleCloseSubmenu}
                                >
                                  {item.items.map((child) => (
                                    <NavLink
                                      key={`${child.to}-${child.label}`}
                                      to={child.to}
                                      onClick={(e) => {
                                        closeMarketingDropdown();
                                        handleNavClick(child.to)(e);
                                      }}
                                      className="block px-3 py-2 text-sm hover:bg-slate-100"
                                    >
                                      {child.label}
                                    </NavLink>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        }
                        return (
                          <NavLink
                            key={`${item.to}-${item.label}`}
                            to={item.to}
                            onClick={(e) => {
                              closeMarketingDropdown();
                              handleNavClick(item.to)(e);
                            }}
                            className="block px-3 py-2 text-sm hover:bg-slate-100"
                          >
                            {item.label}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="relative" ref={manageRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsManageOpen((prev) => !prev);
                      closeManageTicketsSubmenu();
                      closeHrDropdown();
                      setIsOnlineBookingOpen(false);
                      closeMarketingDropdown();
                      setIsSuperAdminOpen(false);
                    }}
                  className={dropdownButtonClass(isManageOpen || isManageRoute)}
                >
                  <NavItemLabel icon={ManageNavIcon} label="Manage" />
                </button>
                {isManageOpen && (
                  <div className="absolute right-0 top-10 w-60 max-w-[260px] rounded-lg bg-white text-slate-900 shadow-lg border border-slate-200 z-30">
                    {desktopManageMenuItems.map((item) => {
                      if (item.type === "submenu") {
                        return (
                          <div
                            key={`manage-${item.label}`}
                            className="relative"
                            onMouseEnter={openManageTicketsSubmenu}
                            onMouseLeave={scheduleCloseManageTicketsSubmenu}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setIsManageTicketsOpen((prev) => !prev);
                              }}
                              onFocus={openManageTicketsSubmenu}
                              className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-slate-100"
                            >
                              <span>{item.label}</span>
                              <span className="text-slate-400">&rsaquo;</span>
                            </button>
                            {isManageTicketsOpen && (
                              <div
                                className="absolute left-full top-0 ml-2 w-56 rounded-lg bg-white text-slate-900 shadow-lg border border-slate-200 z-40"
                                onMouseEnter={openManageTicketsSubmenu}
                                onMouseLeave={scheduleCloseManageTicketsSubmenu}
                              >
                                {item.items.map((child) => {
                                  if (child.to === "/tickets/new") {
                                    return (
                                      <button
                                        key={child.to}
                                        type="button"
                                        onClick={(event) => {
                                          handleOpenNewTicket(event);
                                        }}
                                        className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
                                      >
                                        {child.label}
                                      </button>
                                    );
                                  }
                                  if (child.action === "payment-in") {
                                    return (
                                      <button
                                        key={child.label}
                                        type="button"
                                        onClick={(event) => {
                                          handleOpenPaymentModal("in")(event);
                                        }}
                                        className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
                                      >
                                        {child.label}
                                      </button>
                                    );
                                  }
                                  if (child.action === "payment-out") {
                                    return (
                                      <button
                                        key={child.label}
                                        type="button"
                                        onClick={(event) => {
                                          handleOpenPaymentModal("out")(event);
                                        }}
                                        className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
                                      >
                                        {child.label}
                                      </button>
                                    );
                                  }
                                  return (
                                    <NavLink
                                      key={child.to}
                                      to={child.to}
                                      onClick={(e) => {
                                        closeManageDropdown();
                                        handleNavClick(child.to)(e);
                                      }}
                                      className="block px-3 py-2 text-sm hover:bg-slate-100"
                                    >
                                      {child.label}
                                    </NavLink>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      }
                      return (
                        <NavLink
                          key={`${item.to}-${item.label}`}
                          to={item.to}
                          onClick={(e) => {
                            closeManageDropdown();
                            handleNavClick(item.to)(e);
                          }}
                          className="block px-3 py-2 text-sm hover:bg-slate-100"
                        >
                          {item.label}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>

              {canUseAIAssistant && (
                <button
                  type="button"
                  onClick={handleOpenAIAssistant}
                  className="px-3 py-1.5 rounded-md text-sm font-medium text-slate-300 inline-flex items-center gap-1.5 hover:bg-slate-800"
                >
                  <NavItemLabel icon={AIAssistantNavIcon} label="AI" />
                </button>
              )}
            </>
          )}

          {isAuthenticated && Boolean(user?.is_super_admin) && (
            <div className="relative" ref={superAdminRef}>
              <button
                type="button"
                onClick={() => {
                  setIsSuperAdminOpen((prev) => !prev);
                  closeHrDropdown();
                  setIsManageOpen(false);
                  closeManageTicketsSubmenu();
                  setIsOnlineBookingOpen(false);
                  closeMarketingDropdown();
                }}
                className={dropdownButtonClass(
                  isSuperAdminOpen || isSuperAdminRoute
                )}
              >
                <NavItemLabel icon={SuperAdminNavIcon} label="Super Admin" />
              </button>
              {isSuperAdminOpen && (
                <div className="absolute left-0 top-10 w-44 rounded-lg bg-white text-slate-900 shadow-lg border border-slate-200 z-30">
                  {superAdminMenuItems.map((item) => (
                    <NavLink
                      key={`${item.to}-${item.label}`}
                      to={item.to}
                      onClick={(e) => {
                        setIsSuperAdminOpen(false);
                        handleNavClick(item.to)(e);
                      }}
                      className="block px-3 py-2 text-sm hover:bg-slate-100"
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {isAuthenticated && isTechnician && !canUseOperatorNav && (
            <>
              <Link
                to={technicianSchedulePath}
                className={navPillClass("/technician/schedule")}
              >
                <NavItemLabel icon={CalendarNavIcon} label="Schedule" />
              </Link>
              <div className="relative" ref={techProfileRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsTechProfileOpen((prev) => {
                        const next = !prev;
                        if (!next) {
                          clearTechSalonSpeakCloseTimer();
                          setIsTechSalonSpeakOpen(false);
                        }
                        return next;
                      });
                      setIsManageOpen(false);
                      closeManageTicketsSubmenu();
                      closeMarketingDropdown();
                      setIsSuperAdminOpen(false);
                    }}
                  className={dropdownButtonClass(isTechProfileOpen)}
                >
                  <NavItemLabel icon={ManageNavIcon} label="Manage" />
                </button>
                {isTechProfileOpen && (
                  <div className="absolute mt-2 w-56 rounded-lg bg-white text-slate-900 shadow-lg border border-slate-200 overflow-visible z-30">
                    <Link
                      to="/manager/hr/training"
                      onClick={() => setIsTechProfileOpen(false)}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-slate-100"
                    >
                      Training & Development
                    </Link>
                    {canAccessSalonSpeakModules && (
                      <div
                        className="relative"
                        onMouseEnter={openTechSalonSpeakSubmenu}
                        onMouseLeave={scheduleCloseTechSalonSpeakSubmenu}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setIsTechSalonSpeakOpen((prev) => !prev);
                            clearTechSalonSpeakCloseTimer();
                          }}
                          onFocus={openTechSalonSpeakSubmenu}
                          className="flex w-full items-center justify-between px-4 py-2 text-sm hover:bg-slate-100"
                        >
                          <span>SalonSpeak</span>
                          <span className="text-slate-400">&rsaquo;</span>
                        </button>
                        {isTechSalonSpeakOpen && (
                          <div
                            className="absolute left-full top-0 ml-2 w-56 rounded-lg bg-white text-slate-900 shadow-lg border border-slate-200 z-40"
                            onMouseEnter={openTechSalonSpeakSubmenu}
                            onMouseLeave={scheduleCloseTechSalonSpeakSubmenu}
                          >
                            <NavLink
                              to="/manager/hr/training/salonspeak/home"
                              onClick={() => {
                                setIsTechProfileOpen(false);
                                clearTechSalonSpeakCloseTimer();
                                setIsTechSalonSpeakOpen(false);
                              }}
                              className="block px-3 py-2 text-sm hover:bg-slate-100"
                            >
                              SalonSpeak English
                            </NavLink>
                            <NavLink
                              to="/manager/hr/training/salonspeak-upsell/home"
                              onClick={() => {
                                setIsTechProfileOpen(false);
                                clearTechSalonSpeakCloseTimer();
                                setIsTechSalonSpeakOpen(false);
                              }}
                              className="block px-3 py-2 text-sm hover:bg-slate-100"
                            >
                              SalonSpeak Upsell
                            </NavLink>
                            <NavLink
                              to="/manager/hr/training/salonspeak-reception/home"
                              onClick={() => {
                                setIsTechProfileOpen(false);
                                clearTechSalonSpeakCloseTimer();
                                setIsTechSalonSpeakOpen(false);
                              }}
                              className="block px-3 py-2 text-sm hover:bg-slate-100"
                            >
                              SalonSpeak Reception
                            </NavLink>
                          </div>
                        )}
                      </div>
                    )}
                    <Link
                      to="/manager/manage/maintenance-cleaning"
                      onClick={() => setIsTechProfileOpen(false)}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-slate-100"
                    >
                      Maintenance & Cleaning
                    </Link>
                    <Link
                      to="/bug-report"
                      onClick={() => setIsTechProfileOpen(false)}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-slate-100"
                    >
                      Report a Bug
                    </Link>
                  </div>
                )}
              </div>
              {canUseAIAssistant && (
                <button
                  type="button"
                  onClick={handleOpenAIAssistant}
                  className="px-3 py-1.5 rounded-md text-sm font-medium text-slate-300 inline-flex items-center gap-1.5 hover:bg-slate-800"
                >
                  <NavItemLabel icon={AIAssistantNavIcon} label="AI" />
                </button>
              )}
            </>
          )}

          {isAuthenticated && !isManager && !isTechnician && !isIndependentProfessional && (
            <>
              <Link
                to="/my-bookings"
                className={navPillClass("/my-bookings")}
              >
                My Bookings
              </Link>
              <Link
                to="/manager/hr/training"
                className={navPillClass("/manager/hr/training")}
              >
                Training
              </Link>
            </>
          )}
        </div>

        <div className="ml-auto flex items-center gap-0.5 lg:gap-1 whitespace-nowrap">
          <button
            type="button"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            title={isMenuOpen ? "Close menu" : "Open menu"}
            className="md:hidden h-9 w-9 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-100 inline-flex items-center justify-center"
            onClick={() => {
              setIsMenuOpen((prev) => !prev);
              closeMenus();
            }}
          >
            {isMenuOpen ? (
              <svg
                width="18"
                height="18"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                width="18"
                height="18"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </button>
          {showMyBoothShareLaunch ? (
            <a
              href={marketplaceSwitchHref}
              onClick={handleMarketplaceLaunch}
              className="hidden md:inline-flex h-8 lg:h-9 items-center rounded-md border border-sky-400/40 bg-sky-500/10 px-2 lg:px-3 2xl:px-4 min-[1024px]:max-[1500px]:px-2.5 text-[11px] lg:text-sm min-[1024px]:max-[1500px]:text-[12px] font-semibold text-sky-100 transition hover:bg-sky-500/20"
            >
              Open MyBoothShare
            </a>
          ) : null}
          {isAuthenticated ? (
            <div className="relative hidden md:block" ref={accountMenuRef}>
              <button
                type="button"
                onClick={() => {
                  setIsAccountMenuOpen((prev) => !prev);
                  setIsMenuOpen(false);
                  closeHrDropdown();
                  setIsManageOpen(false);
                  closeManageTicketsSubmenu();
                  setIsOnlineBookingOpen(false);
                  closeMarketingDropdown();
                  setIsSuperAdminOpen(false);
                  setIsTechProfileOpen(false);
                  clearTechSalonSpeakCloseTimer();
                  setIsTechSalonSpeakOpen(false);
                }}
                className="flex min-h-11 max-w-[340px] items-center gap-2 rounded-xl border border-[#44529b] bg-[#1f2757] px-2 py-1 text-left text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
                title={salonContextLabelWithUser}
                aria-expanded={isAccountMenuOpen}
                aria-haspopup="menu"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/50 bg-slate-100 text-[10px] font-semibold text-slate-700">
                  {staffAvatarUrl ? (
                    <img
                      src={staffAvatarUrl}
                      alt={userDisplayName || "Staff"}
                      className="h-full w-full object-cover"
                      loading="eager"
                      onError={handleStaffAvatarError}
                    />
                  ) : (
                    <span aria-hidden="true">{userInitials}</span>
                  )}
                </span>
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block truncate text-xs font-semibold text-white">
                    {userDisplayName || "Signed in user"}
                  </span>
                  <span className="block whitespace-normal break-words text-[10px] leading-tight text-slate-300">
                    {showSalonContext ? salonContextLabel : "No salon selected"}
                  </span>
                </span>
              </button>
              {isAccountMenuOpen ? (
                <div className="absolute right-0 top-full z-[4700] mt-2 w-[min(92vw,26rem)] rounded-2xl border border-slate-200 bg-white p-3 text-slate-900 shadow-2xl">
                  <div className="border-b border-slate-200 px-3 pb-3">
                    <div className="text-sm font-semibold text-slate-900">
                      {userDisplayName || "Signed in user"}
                    </div>
                    <div className="mt-1 whitespace-normal break-words text-xs leading-5 text-slate-500">
                      {showSalonContext ? salonContextLabel : "No salon selected"}
                    </div>
                  </div>
                  {showNavbarSalonSelector ? (
                    <label className="mt-3 block min-w-0 px-3">
                      <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Switch Salon
                      </span>
                      <div className="mt-2 block min-w-0 max-w-full">
                        <select
                          value={user?.salon_id ?? ""}
                          onChange={handleNavbarSalonChange}
                          disabled={isSwitchingSalon}
                          aria-label="Switch salon context"
                          title={salonContextLabel}
                          className="block box-border min-h-11 w-full max-w-full min-w-0 truncate rounded-lg border border-slate-300 px-3 py-2 pr-10 text-[13px] font-semibold leading-5 text-slate-900"
                        >
                          {accessibleSalonList.map((s) => {
                            const optionValue = s.id ?? s.salon_id ?? s.salonId ?? "";
                            return (
                              <option
                                key={optionValue}
                                value={optionValue}
                                style={SALON_SWITCH_OPTION_STYLE}
                              >
                                {isIndependentProfessional
                                  ? formatIndependentProfessionalContextLabel(user, s)
                                  : formatNavSalonLabel(s)}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </label>
                  ) : (
                    <div className="mt-3 px-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Switch Salon
                    </div>
                  )}
                  <div className="mt-3 space-y-1">
                    <Link
                      to="/profile"
                      onClick={() => setIsAccountMenuOpen(false)}
                      className={accountMenuItemClass}
                    >
                      My Profile
                    </Link>
                    <a
                      href={marketplaceSwitchHref}
                      onClick={handleMarketplaceLaunch}
                      className={accountMenuServiceLinkClass}
                    >
                      Open MyBoothShare
                    </a>
                    <a
                      href={accountingSwitchHref}
                      onClick={() => setIsAccountMenuOpen(false)}
                      className={accountMenuServiceLinkClass}
                    >
                      Open SalonBusinessCPA
                    </a>
                    <button
                      type="button"
                      onClick={handleLogout}
                      id="navbar-logout-button-desktop"
                      className={accountMenuItemClass}
                    >
                      Logout
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="h-9 px-3 rounded-md text-sm font-medium border border-slate-600 hover:bg-slate-800"
              >
                Login
              </button>
              <button
                type="button"
                onClick={() => navigate("/register")}
                className="h-9 px-3 rounded-md text-sm font-medium bg-rose-500 hover:bg-rose-600 text-white"
              >
                Register
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        className={`md:hidden fixed inset-0 z-[4600] transition-[visibility] duration-200 ${
          isMenuOpen ? "visible" : "invisible pointer-events-none"
        }`}
      >
        <button
          type="button"
          aria-label="Close menu"
          className={`absolute inset-0 bg-black/45 transition-opacity duration-200 ${
            isMenuOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setIsMenuOpen(false)}
        />
        <aside
          className={`absolute right-0 top-[calc(64px+env(safe-area-inset-top,0px))] h-[calc(100dvh-64px-env(safe-area-inset-top,0px))] w-[78vw] max-w-[340px] overflow-y-auto border-l border-slate-600 bg-gradient-to-b from-[#1f243d] via-[#252c45] to-[#30384a] text-slate-100 shadow-2xl transition-transform duration-200 ${
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
        {isAuthenticated && (
          <div className="border-b border-white/20 px-4 py-3">
            <div className="rounded-sm border border-[#44529b] bg-[#1f2757] text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
              <div className="flex items-center gap-3 px-2.5 py-2">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/50 bg-slate-100 text-sm font-semibold text-slate-700">
                  {staffAvatarUrl ? (
                    <img
                      src={staffAvatarUrl}
                      alt={userDisplayName || "Staff"}
                      className="h-full w-full object-cover"
                      loading="eager"
                      onError={handleStaffAvatarError}
                    />
                  ) : (
                    <span aria-hidden="true">{userInitials}</span>
                  )}
                </span>
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="truncate text-[14px] font-semibold text-white">
                    {userDisplayName || "Signed in user"}
                  </div>
                  {showNavbarSalonSelector ? (
                    <div className="relative mt-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => toggleMobileSection("mobileSalonSwitcher")}
                        disabled={isSwitchingSalon}
                        aria-expanded={Boolean(mobileExpandedSections.mobileSalonSwitcher)}
                        aria-label="Switch salon context"
                        className="flex w-full min-w-0 items-center justify-between gap-2 rounded-md border border-white/10 bg-white/10 px-2 py-1 text-left text-[12px] font-semibold text-white outline-none transition disabled:cursor-wait disabled:opacity-70"
                      >
                        <span className="min-w-0 flex-1 truncate">
                          {salonContextLabel}
                        </span>
                        <span className="shrink-0 text-slate-300" aria-hidden="true">
                          {mobileExpandedSections.mobileSalonSwitcher ? "^" : "v"}
                        </span>
                      </button>
                      {mobileExpandedSections.mobileSalonSwitcher ? (
                        <div className="mt-1 max-h-44 w-full min-w-0 overflow-y-auto rounded-md border border-white/15 bg-[#11162f] p-1 shadow-xl">
                          {accessibleSalonList.map((s) => {
                            const optionValue = s.id ?? s.salon_id ?? s.salonId ?? "";
                            const optionLabel = isIndependentProfessional
                              ? formatIndependentProfessionalContextLabel(user, s)
                              : formatNavSalonLabel(s);
                            const isSelected =
                              String(optionValue) === String(user?.salon_id ?? "");
                            return (
                              <button
                                key={optionValue}
                                type="button"
                                onClick={() => {
                                  setMobileExpandedSections((prev) => ({
                                    ...prev,
                                    mobileSalonSwitcher: false,
                                  }));
                                  handleNavbarSalonChange({
                                    target: { value: optionValue },
                                  });
                                }}
                                className={`block w-full min-w-0 rounded px-2 py-1.5 text-left text-[12px] font-semibold leading-4 ${
                                  isSelected
                                    ? "bg-sky-500/20 text-sky-100"
                                    : "text-slate-100 hover:bg-white/10"
                                }`}
                              >
                                <span className="block max-w-full break-words">
                                  {optionLabel}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="truncate text-[12px] italic text-slate-200/90">
                      {salonContextLabel}
                    </div>
                  )}
                </div>
              </div>
              <div className="border-t border-white/10 p-2">
                <Link
                  to="/profile"
                  onClick={() => setIsMenuOpen(false)}
                  className={mobileAccountMenuItemClass}
                >
                  My Profile
                </Link>
                <a
                  href={marketplaceSwitchHref}
                  onClick={handleMarketplaceLaunch}
                  className={mobileAccountMenuServiceLinkClass}
                >
                  Open MyBoothShare
                </a>
                <a
                  href={accountingSwitchHref}
                  onClick={() => setIsMenuOpen(false)}
                  className={mobileAccountMenuServiceLinkClass}
                >
                  Open SalonBusinessCPA
                </a>
                <button
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleLogout();
                  }}
                  className="mt-1 block w-full rounded-md px-3 py-2 text-left text-sm font-semibold text-rose-100 transition hover:bg-rose-500/15"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="px-4 py-4 space-y-2">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">
            Navigation
          </div>
          {!isAuthenticated &&
            publicBookingLandingLinks.map((item) => (
              <a
                key={`mobile-public-${item.hash}`}
                href={getPublicBookingLandingHref(item.hash)}
                onClick={() => setIsMenuOpen(false)}
                className="block rounded-md px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800 hover:text-white"
              >
                {item.label}
              </a>
            ))}
          {canUseAIAssistant && (
            <button
              type="button"
              onClick={(event) => {
                setIsMenuOpen(false);
                handleOpenAIAssistant(event);
              }}
              className="block pl-3 text-left text-sm text-slate-200 hover:text-white"
            >
              <span className="inline-flex items-center gap-2">
                <AIAssistantNavIcon />
                <span>AI</span>
              </span>
            </button>
          )}
          {isAuthenticated && canUseOperatorNav && (
            <>
              <Link
                to={operatorSchedulePath}
                onClick={(e) => {
                  setIsMenuOpen(false);
                  handleNavClick(operatorSchedulePath)(e);
                }}
                className="block pl-3 text-sm text-slate-200 hover:text-white"
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarNavIcon />
                  <span>Schedule</span>
                </span>
              </Link>
              {canViewReports && (
                <Link
                  to="/manager/reports"
                  onClick={(e) => {
                    setIsMenuOpen(false);
                    handleNavClick("/manager/reports")(e);
                  }}
                  className="block pl-3 text-sm text-slate-200 hover:text-white"
                >
                  <span className="inline-flex items-center gap-2">
                    <ReportsNavIcon />
                    <span>Reports</span>
                  </span>
                </Link>
              )}
              {hrMenuItems.length > 0 && (
                <div className="space-y-1">
                  <button
                    type="button"
                    onClick={() => toggleMobileSection("hr")}
                    className="relative block w-full pl-3 text-left text-sm text-slate-100 hover:text-white"
                  >
                    <MobileMenuChevron
                      isOpen={mobileExpandedSections.hr}
                      className="absolute left-0 top-1/2 -translate-y-1/2 text-sky-400"
                    />
                    <span className="inline-flex items-center gap-2">
                      <HrNavIcon />
                      <span>HR</span>
                    </span>
                  </button>
                  {mobileExpandedSections.hr &&
                    hrMenuItems.map((item) => {
                      if (item.type === "submenu") {
                        return (
                          <div key={`mobile-hr-${item.label}`} className="space-y-1">
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => toggleMobileSection("hrTraining")}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-sky-400"
                                aria-label={`Toggle ${item.label} submenu`}
                              >
                                <MobileMenuChevron
                                  isOpen={mobileExpandedSections.hrTraining}
                                />
                              </button>
                              <Link
                                to={item.to}
                                onClick={(e) => {
                                  setIsMenuOpen(false);
                                  handleNavClick(item.to)(e);
                                }}
                                className="block w-full pl-6 text-sm text-slate-200 hover:text-white"
                              >
                                {item.label}
                              </Link>
                            </div>
                            {mobileExpandedSections.hrTraining &&
                              item.items.map((child) => (
                                <Link
                                  key={`mobile-hr-${child.to}-${child.label}`}
                                  to={child.to}
                                  onClick={(e) => {
                                    setIsMenuOpen(false);
                                    handleNavClick(child.to)(e);
                                  }}
                                  className="block pl-11 text-sm text-slate-200 hover:text-white"
                                >
                                  {child.label}
                                </Link>
                              ))}
                          </div>
                        );
                      }
                      return (
                        <Link
                          key={`mobile-hr-${item.to}-${item.label}`}
                          to={item.to}
                          onClick={(e) => {
                            setIsMenuOpen(false);
                            handleNavClick(item.to)(e);
                          }}
                          className="block pl-6 text-sm text-slate-200 hover:text-white"
                        >
                          {item.label}
                        </Link>
                      );
                    })}
                </div>
              )}
              {onlineBookingMenuItems.length > 0 && (
                <div className="space-y-1">
                  <button
                    type="button"
                    onClick={() => toggleMobileSection("onlineBooking")}
                    className="relative block w-full pl-3 text-left text-sm text-slate-100 hover:text-white"
                  >
                    <MobileMenuChevron
                      isOpen={mobileExpandedSections.onlineBooking}
                      className="absolute left-0 top-1/2 -translate-y-1/2 text-sky-400"
                    />
                    <span className="inline-flex items-center gap-2">
                      <OnlineBookingNavIcon />
                      <span>Online Booking</span>
                    </span>
                  </button>
                  {mobileExpandedSections.onlineBooking &&
                    onlineBookingMenuItems.map((item) => {
                      if (item.href) {
                        return (
                          <a
                            key={`mobile-online-booking-${item.href}-${item.label}`}
                            href={item.href}
                            target={item.external ? "_blank" : undefined}
                            rel={item.external ? "noreferrer" : undefined}
                            onClick={(event) => {
                              if (shouldBlockRoute(item.href)) {
                                handleBlockedNav(event, item.href);
                                return;
                              }
                              setIsMenuOpen(false);
                            }}
                            className="block pl-6 text-sm text-slate-200 hover:text-white truncate"
                          >
                            {item.label}
                          </a>
                        );
                      }
                      return (
                        <Link
                          key={`mobile-online-booking-${item.to}-${item.label}`}
                          to={item.to}
                          onClick={(e) => {
                            setIsMenuOpen(false);
                            handleNavClick(item.to)(e);
                          }}
                          className="block pl-6 text-sm text-slate-200 hover:text-white truncate"
                        >
                          {item.label}
                        </Link>
                      );
                    })}
                </div>
              )}
              {marketingMenuItems.length > 0 && (
                <div className="space-y-1">
                  <button
                    type="button"
                    onClick={() => toggleMobileSection("marketing")}
                    className="relative block w-full pl-3 text-left text-sm text-slate-100 hover:text-white"
                  >
                    <MobileMenuChevron
                      isOpen={mobileExpandedSections.marketing}
                      className="absolute left-0 top-1/2 -translate-y-1/2 text-sky-400"
                    />
                    <span className="inline-flex items-center gap-2">
                      <MarketingNavIcon />
                      <span>Marketing</span>
                    </span>
                  </button>
                  {mobileExpandedSections.marketing &&
                    marketingMenuItems.map((item) => {
                      if (item.type === "submenu") {
                        const mobileSubmenuKey =
                          item.submenuKey === "emails"
                            ? "marketingEmails"
                            : "marketingFeedback";
                        return (
                          <div
                            key={`mobile-marketing-${item.label}`}
                            className="space-y-1"
                          >
                            <button
                              type="button"
                              onClick={() => toggleMobileSection(mobileSubmenuKey)}
                              className="relative block w-full pl-6 text-left text-sm text-slate-200 hover:text-white"
                            >
                              <MobileMenuChevron
                                isOpen={mobileExpandedSections[mobileSubmenuKey]}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-sky-400"
                              />
                              <span>{item.label}</span>
                            </button>
                            {mobileExpandedSections[mobileSubmenuKey] &&
                              item.items.map((child) => (
                                <Link
                                  key={`mobile-marketing-${child.to}-${child.label}`}
                                  to={child.to}
                                  onClick={(e) => {
                                    setIsMenuOpen(false);
                                    handleNavClick(child.to)(e);
                                  }}
                                  className="block pl-11 text-sm text-slate-200 hover:text-white"
                                >
                                  {child.label}
                                </Link>
                              ))}
                          </div>
                        );
                      }
                      return (
                        <Link
                          key={`mobile-marketing-${item.to}-${item.label}`}
                          to={item.to}
                          onClick={(e) => {
                            setIsMenuOpen(false);
                            handleNavClick(item.to)(e);
                          }}
                          className="block pl-6 text-sm text-slate-200 hover:text-white"
                        >
                          {item.label}
                        </Link>
                      );
                    })}
                </div>
              )}
              <div className="space-y-1">
                <button
                  type="button"
                  onClick={() => toggleMobileSection("manage")}
                  className="relative block w-full pl-3 text-left text-sm text-slate-100 hover:text-white"
                >
                  <MobileMenuChevron
                    isOpen={mobileExpandedSections.manage}
                    className="absolute left-0 top-1/2 -translate-y-1/2 text-sky-400"
                  />
                  <span className="inline-flex items-center gap-2">
                    <ManageNavIcon />
                    <span>Manage</span>
                  </span>
                </button>
                {mobileExpandedSections.manage &&
                  manageMenuItems.map((item) => {
                    if (item.type === "submenu") {
                      return (
                        <div key={`mobile-manage-${item.label}`} className="space-y-1">
                          <button
                            type="button"
                            onClick={() => toggleMobileSection("manageTickets")}
                            className="relative block w-full pl-6 text-left text-sm text-slate-200 hover:text-white"
                          >
                            <MobileMenuChevron
                              isOpen={mobileExpandedSections.manageTickets}
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-sky-400"
                            />
                            <span>{item.label}</span>
                          </button>
                          {mobileExpandedSections.manageTickets &&
                            item.items.map((child) => {
                              if (child.to === "/tickets/new") {
                                return (
                                  <button
                                    key={`mobile-${child.to}`}
                                    type="button"
                                    onClick={(event) => {
                                      setIsMenuOpen(false);
                                      handleOpenNewTicket(event);
                                    }}
                                    className="block w-full text-left pl-11 text-sm text-slate-200 hover:text-white"
                                  >
                                    {child.label}
                                  </button>
                                );
                              }
                              if (child.action === "payment-in") {
                                return (
                                  <button
                                    key={`mobile-${child.label}`}
                                    type="button"
                                    onClick={(event) => {
                                      setIsMenuOpen(false);
                                      handleOpenPaymentModal("in")(event);
                                    }}
                                    className="block w-full text-left pl-11 text-sm text-slate-200 hover:text-white"
                                  >
                                    {child.label}
                                  </button>
                                );
                              }
                              if (child.action === "payment-out") {
                                return (
                                  <button
                                    key={`mobile-${child.label}`}
                                    type="button"
                                    onClick={(event) => {
                                      setIsMenuOpen(false);
                                      handleOpenPaymentModal("out")(event);
                                    }}
                                    className="block w-full text-left pl-11 text-sm text-slate-200 hover:text-white"
                                  >
                                    {child.label}
                                  </button>
                                );
                              }
                              return (
                                <Link
                                  key={`mobile-${child.to}`}
                                  to={child.to}
                                  onClick={(e) => {
                                    setIsMenuOpen(false);
                                    handleNavClick(child.to)(e);
                                  }}
                                  className="block pl-11 text-sm text-slate-200 hover:text-white"
                                >
                                  {child.label}
                                </Link>
                              );
                            })}
                        </div>
                      );
                    }
                    return (
                      <Link
                        key={`mobile-manage-${item.to}-${item.label}`}
                        to={item.to}
                        onClick={(e) => {
                          setIsMenuOpen(false);
                          handleNavClick(item.to)(e);
                        }}
                        className="block pl-6 text-sm text-slate-200 hover:text-white"
                      >
                        {item.label}
                      </Link>
                    );
                  })}
              </div>
            </>
          )}

          {isAuthenticated && Boolean(user?.is_super_admin) && (
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => toggleMobileSection("superAdmin")}
                className="relative block w-full pl-3 text-left text-sm text-slate-100 hover:text-white"
              >
                <MobileMenuChevron
                  isOpen={mobileExpandedSections.superAdmin}
                  className="absolute left-0 top-1/2 -translate-y-1/2 text-sky-400"
                />
                <span className="inline-flex items-center gap-2">
                  <SuperAdminNavIcon />
                  <span>Super Admin</span>
                </span>
              </button>
              {mobileExpandedSections.superAdmin &&
                superAdminMenuItems.map((item) => (
                  <Link
                    key={`mobile-superadmin-${item.to}-${item.label}`}
                    to={item.to}
                    onClick={(e) => {
                      setIsMenuOpen(false);
                      handleNavClick(item.to)(e);
                    }}
                    className="block pl-6 text-sm text-slate-200 hover:text-white"
                  >
                    {item.label}
                  </Link>
                ))}
            </div>
          )}

          {isAuthenticated && isTechnician && !canUseOperatorNav && (
            <>
              <Link
                to={technicianSetupPath}
                onClick={() => setIsMenuOpen(false)}
                className="block pl-3 text-sm text-slate-200 hover:text-white"
              >
                My Profile
              </Link>
              <div className="space-y-1">
                <Link
                  to="/manager/hr/training"
                  onClick={() => setIsMenuOpen(false)}
                  className="block pl-3 text-sm text-slate-200 hover:text-white"
                >
                  Training & Development
                </Link>
                {canAccessSalonSpeakModules && (
                  <div className="space-y-1">
                    <button
                      type="button"
                      onClick={() => toggleMobileSection("techSalonSpeak")}
                      className="relative block w-full pl-6 text-left text-sm text-slate-200 hover:text-white"
                    >
                      <MobileMenuChevron
                        isOpen={mobileExpandedSections.techSalonSpeak}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-sky-400"
                      />
                      <span>SalonSpeak</span>
                    </button>
                    {mobileExpandedSections.techSalonSpeak && (
                      <div className="ml-6 space-y-1 border-l border-slate-500/60 pl-3">
                        <Link
                          to="/manager/hr/training/salonspeak/home"
                          onClick={() => setIsMenuOpen(false)}
                          className="block text-sm text-slate-300 hover:text-white"
                        >
                          SalonSpeak English
                        </Link>
                        <Link
                          to="/manager/hr/training/salonspeak-upsell/home"
                          onClick={() => setIsMenuOpen(false)}
                          className="block text-sm text-slate-300 hover:text-white"
                        >
                          SalonSpeak Upsell
                        </Link>
                        <Link
                          to="/manager/hr/training/salonspeak-reception/home"
                          onClick={() => setIsMenuOpen(false)}
                          className="block text-sm text-slate-300 hover:text-white"
                        >
                          SalonSpeak Reception
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <Link
                to="/manager/manage/daily-tasks"
                onClick={() => setIsMenuOpen(false)}
                className="block pl-3 text-sm text-slate-200 hover:text-white"
              >
                Daily Tasks
              </Link>
              <Link
                to="/manager/manage/maintenance-cleaning"
                onClick={() => setIsMenuOpen(false)}
                className="block pl-3 text-sm text-slate-200 hover:text-white"
              >
                Maintenance & Cleaning
              </Link>
              <Link
                to={technicianSchedulePath}
                onClick={() => setIsMenuOpen(false)}
                className="block pl-3 text-sm text-slate-200 hover:text-white"
              >
                Schedule
              </Link>
            </>
          )}

          {isAuthenticated && !isManager && !isTechnician && !isIndependentProfessional && (
            <>
              <Link
                to="/my-bookings"
                onClick={() => setIsMenuOpen(false)}
                className="block pl-3 text-sm text-slate-200 hover:text-white"
              >
                My Bookings
              </Link>
              <Link
                to="/manager/hr/training"
                onClick={() => setIsMenuOpen(false)}
                className="block pl-3 text-sm text-slate-200 hover:text-white"
              >
                Training
              </Link>
            </>
          )}

        </div>
        <div className="px-4 py-4 space-y-2 border-t border-white/20">
          {isAuthenticated ? (
            <>
              {!isManager && (
                <Link
                  to="/change-password"
                  onClick={() => setIsMenuOpen(false)}
                  className="block text-sm text-slate-200 hover:text-white"
                >
                  Change Password
                </Link>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate("/login");
                }}
                className="block text-left text-sm text-slate-200 hover:text-white"
              >
                Login
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate("/register");
                }}
                className="block text-left text-sm text-slate-200 hover:text-white"
              >
                Register
              </button>
            </>
          )}
        </div>
        </aside>
      </div>
    </header>
    {navGateMsg && (
      <div className="fixed top-16 left-1/2 z-[4000] -translate-x-1/2 rounded-full bg-slate-900/90 px-4 py-2 text-sm text-white shadow-lg">
        {navGateMsg}
      </div>
    )}
    {showLogoutGate && (
      <div className="fixed inset-0 z-[5000] bg-black/40 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-slate-200">
          <div className="p-5">
            <h3 className="text-lg font-semibold text-slate-900">Leave setup?</h3>
            <p className="mt-2 text-sm text-slate-600">
              You haven&rsquo;t completed the Salon / Independent Professionals Setup Wizard yet.
              What would you like to do?
            </p>
          </div>
          <div className="px-5 pb-5 flex flex-col gap-2">
            <button
              type="button"
              className="w-full rounded-lg bg-slate-900 text-white py-2 text-sm font-medium"
              onClick={async () => {
                setShowLogoutGate(false);
                setNavGateMsg("Your progress is saved. Logging out now.");
                await logout();
                navigate("/login");
              }}
            >
              Save progress & Logout
            </button>
            <button
              type="button"
              className="w-full rounded-lg border border-rose-300 bg-rose-50 text-rose-700 py-2 text-sm font-medium"
              onClick={handleCancelSetupAndExit}
            >
              Cancel setup & Exit
            </button>
            <button
              type="button"
              className="w-full rounded-lg border border-slate-200 bg-white text-slate-900 py-2 text-sm font-medium"
              onClick={() => {
                setShowLogoutGate(false);
                navigate("/setup", {
                  replace: true,
                  state: {
                    onboardingGateMessage: ONBOARDING_GATE_MESSAGE,
                  },
                });
              }}
            >
              Continue setup
            </button>
          </div>
        </div>
      </div>
    )}
    <SBINModal
      isOpen={showBlockedNavModal}
      onClose={() => setShowBlockedNavModal(false)}
      title="Select a salon to continue"
      size="sm"
      fitContent={true}
      closeOnBackdrop={true}
      closeOnEscape={true}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setShowBlockedNavModal(false)}
            className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleGoToSuperAdminConsole}
            className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600"
          >
            Go to Super Admin Console
          </button>
        </div>
      }
    >
      <div className="space-y-3 text-sm text-slate-700">
        <p>
          You&apos;ve selected a business, but you haven&apos;t switched into a salon yet.
        </p>
        <p>
          To manage schedules, reports, and settings, please click{" "}
          <span className="font-semibold">Switch to Salon</span> in the Super Admin
          Console.
        </p>
      </div>
    </SBINModal>
    <SBINModal
      isOpen={showAccessGateModal}
      onClose={() => setShowAccessGateModal(false)}
      title={accessGate?.title || "Complete setup to continue"}
      size="sm"
      fitContent={true}
      closeOnBackdrop={true}
      closeOnEscape={true}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setShowAccessGateModal(false)}
            className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleResolveAccessGate}
            className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600"
          >
            {accessGate?.ctaLabel || "Continue"}
          </button>
        </div>
      }
    >
      <div className="space-y-3 text-sm text-slate-700">
        <p>{accessGate?.message || ONBOARDING_GATE_MESSAGE}</p>
        <p>
          Complete the required steps first, then the rest of the Ellyanna navigation
          will unlock automatically.
        </p>
      </div>
    </SBINModal>
    <PaymentInOutModal
      isOpen={Boolean(paymentModalMode)}
      mode={paymentModalMode}
      onClose={closePaymentModal}
    />
    <AIAssistantModal
      isOpen={isAIAssistantOpen}
      onClose={() => {
        setIsAIAssistantOpen(false);
        setAiPrefillMessage("");
        setAiContextOverride(null);
      }}
      initialPrefillMessage={aiPrefillMessage}
      initialContext={aiContextOverride}
    />
    </>
  );
}



